/**
 * SettingsLocation — Iter 191t · Selling › Location & Hours
 *
 * Storefront physical location (used for pickup / local delivery / map
 * blocks / tax jurisdictions) plus a weekly Business Hours grid.
 *
 * Persistence:
 *   GET  /api/builder/settings          → returns `location`
 *   PUT  /api/builder/settings { location: {...} }
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MapPin, Save, Loader2, Clock, Building2, Phone } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GlassCard, DashboardBackdrop, GradientIconTile } from '@/components/dashboard/dashboard-ui';
import BackToSettings from './_BackToSettings';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DAYS = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const DEFAULT_HOURS = DAYS.reduce((acc, d) => {
  acc[d.key] = {
    closed: d.key === 'sun',
    open: '09:00',
    close: '17:00',
  };
  return acc;
}, {});

const DEFAULTS = {
  business_name: '',
  address_line1: '',
  address_line2: '',
  city: '',
  region: '',
  postal_code: '',
  country: 'US',
  phone: '',
  timezone: 'America/Los_Angeles',
  show_on_storefront: true,
  hours: DEFAULT_HOURS,
};

const TIMEZONES = [
  'America/Los_Angeles', 'America/Denver', 'America/Chicago', 'America/New_York',
  'America/Toronto', 'America/Vancouver', 'America/Edmonton', 'America/Winnipeg',
  'America/Halifax', 'America/St_Johns', 'America/Mexico_City',
  'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
  'Australia/Sydney', 'Pacific/Auckland', 'Asia/Tokyo', 'Asia/Singapore',
];

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'CA', name: 'Canada' },
  { code: 'MX', name: 'Mexico' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'AU', name: 'Australia' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'JP', name: 'Japan' },
  { code: 'other', name: 'Other' },
];

export default function SettingsLocation() {
  const [state, setState] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/settings`, { headers: auth() });
        const merged = {
          ...DEFAULTS,
          ...(data?.location || {}),
          hours: { ...DEFAULT_HOURS, ...((data?.location || {}).hours || {}) },
        };
        setState(merged);
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const set = (key, val) => setState((p) => ({ ...p, [key]: val }));
  const setHour = (day, key, val) =>
    setState((p) => ({
      ...p,
      hours: {
        ...p.hours,
        [day]: { ...(p.hours?.[day] || {}), [key]: val },
      },
    }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/builder/settings`, { location: state }, { headers: auth() });
      toast.success('Location & hours saved');
    } catch { toast.error('Could not save location'); }
    finally { setSaving(false); }
  };

  return (
    <div className="relative" data-testid="settings-location-page">
      <DashboardBackdrop />
      <div className="max-w-3xl mx-auto py-10 px-4 space-y-6">
        <BackToSettings />

        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.18em] font-bold uppercase text-[#A78BFA]">Selling</p>
            <h1 className="text-3xl font-bold text-white font-syne mt-1">Location &amp; Hours</h1>
            <p className="text-sm text-white/65 mt-2 max-w-xl">
              This address powers your Map block, local-pickup at checkout, packing-slip return address,
              and helps the platform apply the right taxes and shipping rules.
            </p>
          </div>
          <button
            onClick={save}
            disabled={saving || loading}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm bg-gradient-to-r from-[#8B5CF6] to-[#3B82F6] text-white shadow-[0_0_15px_rgba(139,92,246,0.3)] hover:shadow-[0_0_25px_rgba(139,92,246,0.5)] disabled:opacity-50 transition-all"
            data-testid="location-save-btn"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>

        {loading ? (
          <div className="text-white/50 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Storefront address */}
            <GlassCard className="p-6" data-testid="location-address-card">
              <div className="flex items-start gap-4 mb-5">
                <GradientIconTile icon={MapPin} ramp="violet" />
                <div>
                  <h2 className="text-white font-bold font-syne">Storefront address</h2>
                  <p className="text-xs text-white/55 mt-0.5">Where your business physically operates from — buyers see this on the Map block and checkout pickup.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-[#94A3B8] text-xs flex items-center gap-1.5"><Building2 size={11} /> Business name</Label>
                  <Input value={state.business_name} onChange={(e) => set('business_name', e.target.value)} placeholder="Your studio, store, or brand" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="location-business-name" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[#94A3B8] text-xs">Street address</Label>
                  <Input value={state.address_line1} onChange={(e) => set('address_line1', e.target.value)} placeholder="123 Cedar Rd" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="location-address1" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-[#94A3B8] text-xs">Apt / Suite (optional)</Label>
                  <Input value={state.address_line2} onChange={(e) => set('address_line2', e.target.value)} placeholder="Suite 4B" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="location-address2" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">City</Label>
                  <Input value={state.city} onChange={(e) => set('city', e.target.value)} placeholder="Portland" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="location-city" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">State / Province</Label>
                  <Input value={state.region} onChange={(e) => set('region', e.target.value)} placeholder="OR" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="location-region" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Postal / ZIP</Label>
                  <Input value={state.postal_code} onChange={(e) => set('postal_code', e.target.value)} placeholder="97205" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="location-postal" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Country</Label>
                  <select
                    value={state.country}
                    onChange={(e) => set('country', e.target.value)}
                    className="w-full mt-1.5 bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm"
                    data-testid="location-country"
                  >
                    {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs flex items-center gap-1.5"><Phone size={11} /> Public phone</Label>
                  <Input value={state.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+1 (555) 555-1212" className="mt-1.5 bg-white/5 border-white/10 text-white rounded-xl" data-testid="location-phone" />
                </div>
                <div>
                  <Label className="text-[#94A3B8] text-xs">Timezone</Label>
                  <select
                    value={state.timezone}
                    onChange={(e) => set('timezone', e.target.value)}
                    className="w-full mt-1.5 bg-white/5 border border-white/10 text-white rounded-xl px-3 py-2.5 text-sm"
                    data-testid="location-timezone"
                  >
                    {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
                  </select>
                </div>
              </div>

              <label className="mt-5 flex items-center gap-3 text-sm text-white/80 cursor-pointer" data-testid="location-toggle-public">
                <input type="checkbox" checked={state.show_on_storefront} onChange={(e) => set('show_on_storefront', e.target.checked)} className="accent-[#A78BFA]" />
                Show this address publicly on my storefront footer &amp; contact page.
              </label>
            </GlassCard>

            {/* Business hours */}
            <GlassCard className="p-6" data-testid="location-hours-card">
              <div className="flex items-start gap-4 mb-5">
                <GradientIconTile icon={Clock} ramp="cyan" />
                <div>
                  <h2 className="text-white font-bold font-syne">Business hours</h2>
                  <p className="text-xs text-white/55 mt-0.5">
                    Buyers see these times on your storefront and in booking confirmation emails.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                {DAYS.map((d) => {
                  const h = state.hours?.[d.key] || { closed: false, open: '09:00', close: '17:00' };
                  return (
                    <div
                      key={d.key}
                      className="grid grid-cols-[110px_auto_1fr] gap-3 items-center py-2 border-b border-white/[0.05] last:border-0"
                      data-testid={`location-hours-${d.key}`}
                    >
                      <div className="text-sm text-white">{d.label}</div>
                      <label className="flex items-center gap-2 text-[11px] text-white/60">
                        <input
                          type="checkbox"
                          checked={h.closed}
                          onChange={(e) => setHour(d.key, 'closed', e.target.checked)}
                          className="accent-[#A78BFA]"
                          data-testid={`location-hours-${d.key}-closed`}
                        />
                        Closed
                      </label>
                      {!h.closed ? (
                        <div className="flex items-center gap-2 justify-end">
                          <input
                            type="time"
                            value={h.open}
                            onChange={(e) => setHour(d.key, 'open', e.target.value)}
                            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                            data-testid={`location-hours-${d.key}-open`}
                          />
                          <span className="text-white/40 text-xs">to</span>
                          <input
                            type="time"
                            value={h.close}
                            onChange={(e) => setHour(d.key, 'close', e.target.value)}
                            className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs text-white"
                            data-testid={`location-hours-${d.key}-close`}
                          />
                        </div>
                      ) : (
                        <div className="text-right text-[11px] text-white/40 italic">Closed all day</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </GlassCard>
          </>
        )}
      </div>
    </div>
  );
}
